import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { useKitchen } from "@/routes/_authenticated/menus/-lib/use-kitchen";
import { SpendTable } from "./-components/spend-table";

export const Route = createFileRoute("/_authenticated/supplies/spend")({ component: SpendPage });

/** What Nancy's orders cost the building, month by month, with the kitchen's own total. */
function SpendPage() {
  const { facilityId } = useKitchen();
  const seesSpend = useCan("nancy:read", facilityId || null) && facilityId !== "";
  const spend = useQuery({
    ...orpc.agents.supplies.spend.queryOptions({ input: { facilityId, months: 6 } }),
    enabled: seesSpend,
  });
  if (!seesSpend) {
    return (
      <EmptyState
        title="Spend is for those who see Nancy's orders"
        description="Ask an administrator for Nancy access in this building to see what her orders cost."
      />
    );
  }
  return (
    <section className="space-y-3">
      <p className="text-muted-foreground text-sm">
        What Nancy's placed orders cost, by what they bought: food from the menus, and each kind of
        supplies. The kitchen's total is its food and its kitchen supplies together.
      </p>
      {spend.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : spend.isError ? (
        <ReadFailed
          message="Couldn't add up the spend."
          error={spend.error}
          onRetry={() => void spend.refetch()}
        />
      ) : spend.data.total.every((cents) => cents === 0) ? (
        <EmptyState
          title="Nothing ordered through Nancy yet"
          description="Spend shows here once Nancy places an order for this building."
        />
      ) : (
        <>
          <SpendTable spend={spend.data} />
          {spend.data.unpriced > 0 ? (
            <p className="text-muted-foreground text-xs">
              {spend.data.unpriced === 1
                ? "1 line had no price and is left out."
                : `${String(spend.data.unpriced)} lines had no price and are left out.`}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
